/**
 * 应用内 Toast（禁止使用 window.alert）。
 * 任何请求失败都必须经由这里或页面 ErrorState 可见地告知用户。
 */
import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastItem {
  id: string;
  kind: ToastKind;
  message: string;
  detail?: string;
}

interface ToastState {
  items: ToastItem[];
  push: (kind: ToastKind, message: string, detail?: string) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const MAX_TOASTS = 4;
const ERROR_TTL_MS = 9000;
const NORMAL_TTL_MS = 4000;

let sequence = 0;

function nextId(): string {
  sequence += 1;
  return `toast-${sequence}-${Date.now().toString(36)}`;
}

export const useToast = create<ToastState>((set, get) => ({
  items: [],

  push: (kind, message, detail) => {
    const id = nextId();
    const item: ToastItem = detail ? { id, kind, message, detail } : { id, kind, message };
    set((state) => ({ items: [...state.items, item].slice(-MAX_TOASTS) }));
    window.setTimeout(
      () => {
        get().dismiss(id);
      },
      kind === 'error' ? ERROR_TTL_MS : NORMAL_TTL_MS,
    );
    return id;
  },

  dismiss: (id) => set((state) => ({ items: state.items.filter((item) => item.id !== id) })),

  clear: () => set({ items: [] }),
}));

/** 非 React 代码（API 客户端、query 缓存回调）直接调用这三个方法 */
export const toast = {
  success: (message: string, detail?: string): string => useToast.getState().push('success', message, detail),
  error: (message: string, detail?: string): string => useToast.getState().push('error', message, detail),
  info: (message: string, detail?: string): string => useToast.getState().push('info', message, detail),
};